import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import styles from './styles.module.scss'

import { setInterfaceLang } from '../../redux/actions'
import { connect } from 'react-redux'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Button } from '../../components/UI'
import { setValueToSelect } from '../../helpers'
import { INTERFACE_LANGUAGES_OPTIONS } from '../../config'
import {
  GENDER_OPTIONS,
  FORM_CONSTANTS,
  SOCIAL_MEDIA,
  INITIAL_FORM_ERRORS,
  FORM_CONFIG,
  FORM_SECTIONS,
  SPECIAL_SECTIONS,
} from './config'
import { translations } from '../../i18n'
import UploadAvatar from '../../components/upload-avatar'
import FormSection from './components/FormSection'
import useFormHandlers from './hooks/useFormHandlers'

import { handleLoadAvatar } from '../team-create-change/const'

const ProfileSettings = props => {
  const { intl, history, interfaceLang } = props
  const userData = useSelector(state => state.userData)
  const countriesList = useSelector(state => state.countriesList)
  const [formFields, setFormFields] = useState({})
  const [errors, setErrors] = useState(INITIAL_FORM_ERRORS)
  const [loadedImg, setLoadedImg] = useState(null)
  const [sectionsToExpand, setSectionsToExpand] = useState([])

  const {
    inputHandler,
    selectHandler,
    onLanguageChange,
    onSubmit,
    activateAccount,
    changePassword,
  } = useFormHandlers(
    formFields,
    setFormFields,
    setErrors,
    loadedImg,
    setSectionsToExpand
  )

  useEffect(() => {
    if (userData) {
      setUserSettingState()
    } else {
      setFormFields({ ...formFields, error: true })
    }
  }, [userData, countriesList])

  const setUserSettingState = () => {
    const getCountrySelectObject = countryCode => {
      if (!countryCode) return []

      const country = countriesList?.find(c => c.value === countryCode)

      return country ? [{ label: country.label, value: country.value }] : []
    }

    setFormFields({
      ...userData,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      nickname: userData.nickname || '',
      regions: userData.region || '',
      languages: [],
      birthDate: (userData.birthDate && userData.birthDate) || '',
      gender: setValueToSelect(userData.gender) || [],
      citizenship: getCountrySelectObject(userData.citizenship),
      country: getCountrySelectObject(userData.country),
      city: userData.city || '',
      about: userData.about ? userData.about : '',
      discord: userData.discord ? userData.discord : '',
      facebookLink: userData?.sites[0]?.url
        ? userData.sites[0].url
        : SOCIAL_MEDIA.FACEBOOK.DEFAULT_URL,
      url: userData.url ? userData.url : '',
    })
  }

  const onImageSelect = event => {
    const file = event.target.files[0]

    if (file) {
      handleLoadAvatar(file, setLoadedImg)
    }
  }

  const optionsMap = useMemo(
    () => ({
      genderOptions: GENDER_OPTIONS.map(option => ({
        label: intl.formatMessage({ id: option.label }),
        value: option.value,
      })),
      countriesList: countriesList || [],
      INTERFACE_LANGUAGES_OPTIONS: INTERFACE_LANGUAGES_OPTIONS || [],
    }),
    [intl, countriesList]
  )

  const selectedLang = useMemo(() => {
    const currentLang =
      interfaceLang?.toLowerCase() || FORM_CONSTANTS.DEFAULT_LANGUAGE
    const isValidLang = Object.keys(translations).includes(currentLang)
    const languageToUse = isValidLang
      ? currentLang
      : FORM_CONSTANTS.DEFAULT_LANGUAGE

    const selectedOption = INTERFACE_LANGUAGES_OPTIONS.find(
      item => item.value === languageToUse
    ) || { label: '', value: '' }

    return [selectedOption]
  }, [interfaceLang])

  useEffect(() => {
    if (selectedLang[0]?.value) {
      setFormFields(prev => ({ ...prev, language: selectedLang[0].value }))
    }
  }, [selectedLang])

  const handleSectionExpansionChange = useCallback((sectionId, isExpanded) => {
    setSectionsToExpand(prev => {
      if (isExpanded) {
        return prev.includes(sectionId) ? prev : [...prev, sectionId]
      } else {
        return prev.filter(id => id !== sectionId)
      }
    })
  }, [])

  // Clear sections to expand when there are no errors
  useEffect(() => {
    const hasErrors = Object.values(errors).some(error => error.length > 0)
    if (!hasErrors) {
      setSectionsToExpand([])
    }
  }, [errors])

  return (
    <div className={styles.profileSettings}>
      <main>
        <div className={styles.mainContainer}>
          {/* Left Column - Form Sections */}
          <div className={styles.leftColumn}>
            <form onSubmit={onSubmit}>
              {/* Personal Information Section */}
              <FormSection
                section={FORM_CONFIG[FORM_SECTIONS.PERSONAL_INFO]}
                formFields={formFields}
                errors={errors}
                onChange={inputHandler}
                onSelectChange={selectHandler}
                optionsMap={optionsMap}
                userData={userData}
                onLanguageChange={onLanguageChange}
                collapsible={true}
                defaultExpanded={true}
                forceExpanded={sectionsToExpand.includes(
                  FORM_SECTIONS.PERSONAL_INFO
                )}
                onExpansionChange={isExpanded =>
                  handleSectionExpansionChange(
                    FORM_SECTIONS.PERSONAL_INFO,
                    isExpanded
                  )
                }
              />

              {/* Contact Information Section */}
              <FormSection
                section={FORM_CONFIG[FORM_SECTIONS.CONTACT_INFO]}
                formFields={{ ...formFields, language: selectedLang }}
                errors={errors}
                onChange={inputHandler}
                onSelectChange={selectHandler}
                optionsMap={optionsMap}
                userData={userData}
                onLanguageChange={onLanguageChange}
                collapsible={true}
                defaultExpanded={false}
                forceExpanded={sectionsToExpand.includes(
                  FORM_SECTIONS.CONTACT_INFO
                )}
                onExpansionChange={isExpanded =>
                  handleSectionExpansionChange(
                    FORM_SECTIONS.CONTACT_INFO,
                    isExpanded
                  )
                }
              />

              {/* Email activation section */}
              <FormSection
                section={SPECIAL_SECTIONS.EMAIL_ACTIVATION}
                formFields={formFields}
                errors={errors}
                userData={userData}
                activateAccount={activateAccount}
                collapsible={false}
              />

              {/* About section */}
              <FormSection
                section={FORM_CONFIG[FORM_SECTIONS.ADDITIONAL_INFO]}
                formFields={formFields}
                errors={errors}
                onChange={inputHandler}
                userData={userData}
                collapsible={true}
                defaultExpanded={false}
              />
            </form>
          </div>

          {/* Right Column - Photo and Password */}
          <div className={styles.rightColumn}>
            {/* Photo Section */}
            <div className={styles.photoSection}>
              <UploadAvatar
                user={userData}
                image={userData?.avatar}
                conditionRendering={node => node}
                onImageSelect={onImageSelect}
              />
            </div>

            {/* Password change section */}
            <FormSection
              section={SPECIAL_SECTIONS.PASSWORD_CHANGE}
              formFields={formFields}
              errors={errors}
              userData={userData}
              changePassword={changePassword}
              collapsible={false}
            />
          </div>
        </div>
      </main>

      <footer>
        <div className={styles.buttonContainer}>
          <Button
            label={<FormattedMessage id='profile.cancelButton' />}
            type='button'
            color='white'
            active
            size='md'
            variant='settingsCancel'
            action={() => history.push(`/id/${userData?.url}`)}
          />
          <Button
            label={<FormattedMessage id='profile.editButton' />}
            active
            variant='settingsSave'
            size='md'
            action={onSubmit}
          />
        </div>
      </footer>
    </div>
  )
}
const mapStateToProps = state => {
  return {
    countriesList: state.countriesList,
    userData: state.userData,
    interfaceLang: state.interfaceLang,
    languagesList: state.languagesList,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setInterfaceLang: lang => dispatch(setInterfaceLang(lang)),
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(ProfileSettings)
)
